import React, {useState} from 'react';
import {Modal, NumberInput, Textarea, TextInput} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {DateTimePicker} from '@mantine/dates';
import type {CourseClassroomResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import type {ISelectOption} from 'src/types/Form';

export interface ScheduleEditModalProps {
  opened: boolean;
  onClose: () => void;
  classroom: CourseClassroomResponse;
  isSaving: boolean;
  onSave: (updates: {
    startsAt?: Date;
    endsAt?: Date;
    location?: string;
    instructorId?: number;
    capacity?: number;
    notes?: string;
  }) => void;
}

/**
 * Inline schedule edit modal for the Class Workspace sidebar InfoCard.
 * Never touches the roster — the caller must merge these fields onto the
 * classroom's current full attendee list before calling `updateCourseClassroom`
 * (Update still full-replaces attendees from `assignmentIds` via MergeList).
 */
export const ScheduleEditModal: React.FC<ScheduleEditModalProps> = ({
  opened,
  onClose,
  classroom,
  isSaving,
  onSave,
}) => {
  const [startsAt, setStartsAt] = useState<Date | null>(
    classroom.startsAt ? parseUtcDate(classroom.startsAt) : null
  );
  const [endsAt, setEndsAt] = useState<Date | null>(
    classroom.endsAt ? parseUtcDate(classroom.endsAt) : null
  );
  const [location, setLocation] = useState(classroom.location ?? '');
  const [instructorId, setInstructorId] = useState<string | undefined>(
    classroom.instructorId ? String(classroom.instructorId) : undefined
  );
  const [capacity, setCapacity] = useState<number | ''>(
    classroom.capacity ?? ''
  );
  const [notes, setNotes] = useState(classroom.notes ?? '');

  const handleSave = () => {
    onSave({
      startsAt: startsAt ?? undefined,
      endsAt: endsAt ?? undefined,
      location: location.trim() || undefined,
      instructorId: instructorId ? Number(instructorId) : undefined,
      capacity: capacity !== '' ? capacity : undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit Schedule" centered>
      <Stack gap="md">
        <Group grow align="flex-start">
          <DateTimePicker
            label="Starts"
            placeholder="Select start date/time"
            value={startsAt}
            onChange={setStartsAt}
            clearable
            valueFormat="MM/DD/YYYY hh:mm A"
          />
          <DateTimePicker
            label="Ends"
            placeholder="Select end date/time"
            value={endsAt}
            onChange={setEndsAt}
            clearable
            minDate={startsAt ?? undefined}
            valueFormat="MM/DD/YYYY hh:mm A"
          />
        </Group>
        <TextInput
          label="Location"
          placeholder="e.g. Building 4 Training Room"
          value={location}
          onChange={event => setLocation(event.currentTarget.value)}
        />
        <Group grow align="flex-start">
          <PersonSelectBox
            label="Instructor"
            placeholder="Select instructor"
            value={instructorId}
            onSelectChangeCallback={(value: ISelectOption | null) =>
              setInstructorId(value?.id ? String(value.id) : undefined)
            }
          />
          <NumberInput
            label="Capacity"
            placeholder="e.g. 20"
            min={1}
            value={capacity}
            onChange={value => setCapacity(value === '' ? '' : Number(value))}
          />
        </Group>
        <Textarea
          label="Notes"
          placeholder="Anything the trainer or roster builder should know"
          minRows={3}
          value={notes}
          onChange={event => setNotes(event.currentTarget.value)}
        />
      </Stack>
      <Group justify="end" mt="lg">
        <Button variant="subtle" color="black" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={isSaving}>
          Save
        </Button>
      </Group>
    </Modal>
  );
};
